import "../Styles/ReferralCards.css";
import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  Calendar,
  Gift,
  Users,
  IndianRupee,
  CheckCircle,
  Clock,
  XCircle,
  Info,
  Wallet,
} from "lucide-react";

const Referrals = () => {
  const [user] = useState(JSON.parse(localStorage.getItem("student")));
  const [activeTab, setActiveTab] = useState("all");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [hasAutoFlipped, setHasAutoFlipped] = useState(false);
  const [showPopupForm, setShowPopupForm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState("stages");
  const [referralData, setReferralData] = useState({
    name: user.student.studentName,
    referralName: "",
    referralNumber: "",
    UID: user.student.UID,
  });
  const [submitting, setSubmitting] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  // Fetch referrals data
  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADMIN_BASE_URL}/api/get-referrals`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ UID: user.student.UID }),
        }
      );

      const result = await response.json();
      setReferrals(result?.data || []);

      // Calculate wallet balance from completed referrals
      const completedReferrals =
        result?.data?.filter(
          (ref) => ref.status?.toLowerCase() === "completed"
        ) || [];
      setWalletBalance(completedReferrals.length * rewardAmount);
    } catch (error) {
      console.error("Error fetching referrals:", error);
      setReferrals([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  // Auto-flip animation
  useEffect(() => {
    if (referrals.length > 0 && !hasAutoFlipped) {
      setTimeout(() => {
        setFlippedCards((prev) => new Set(prev).add(referrals[0]._id));

        setTimeout(() => {
          setFlippedCards((prev) => {
            const newSet = new Set(prev);
            newSet.delete(referrals[0]._id);
            return newSet;
          });
          setHasAutoFlipped(true);
        }, 2000);
      }, 1000);
    }
  }, [referrals, hasAutoFlipped]);

  // Flip handler
  const handleCardFlip = (cardId) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev);
      newSet.has(cardId) ? newSet.delete(cardId) : newSet.add(cardId);
      return newSet;
    });
  };

  // Status color + icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return <CheckCircle size={16} className="status-icon completed" />;
      case "pending":
        return <Clock size={16} className="status-icon pending" />;
      case "rejected":
        return <XCircle size={16} className="status-icon rejected" />;
      case "yet to join":
        return <Clock size={16} className="status-icon yet-to-join" />;
      case "joined":
        return <CheckCircle size={16} className="status-icon joined" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "status-completed";
      case "pending":
        return "status-pending";
      case "rejected":
        return "status-rejected";
      case "yet to join":
        return "status-yet-to-join";
      case "joined":
        return "status-joined";
      default:
        return "status-pending";
    }
  };

  const rewardAmount =
    user.student.program === "tech-pro"
      ? 1000
      : user.student.program === "tech-smart"
      ? 500
      : user.student.program === "data-science"
      ? 1000
      : 0;

  const calculateEarnings = () => {
    return (
      referrals.filter((ref) => ref.status?.toLowerCase() === "completed")
        .length * rewardAmount
    );
  };

  // Referral form logic
  const handleReferralChange = (e) => {
    setReferralData({ ...referralData, [e.target.name]: e.target.value });
  };

  const submitReferral = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADMIN_BASE_URL}/api/submit-referral`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(referralData),
        }
      );

      const result = await response.json();
      if (result.success) {
        setReferralData({
          name: user.student.studentName,
          referralName: "",
          referralNumber: "",
          UID: user.student.UID,
        });
        setShowPopupForm(false);
        setSuccessPopup(true);
        fetchReferrals(); // refresh list
      } else {
        alert("Failed to submit referral. You already referred this student.");
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setSubmitting(false);
      setTimeout(() => setSuccessPopup(false), 2000);
    }
  };

  // Withdraw request function
  const handleWithdrawRequest = async () => {
    if (walletBalance === 0) {
      alert("No balance available for withdrawal");
      return;
    }

    setWithdrawLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/withdraw-request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UID: user.student.UID,
            studentName: user.student.studentName,
            amount: walletBalance,
            timestamp: new Date().toISOString(),
          }),
        }
      );

      const result = await response.json();
      if (result.success) {
        alert("Withdrawal request submitted successfully!");
        setWalletBalance(0); // Reset balance after successful request
      } else {
        alert("Failed to submit withdrawal request: " + result.message);
      }
    } catch (error) {
      alert("Error submitting withdrawal request: " + error.message);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Filtered list
  const getFilteredReferrals = () => {
    if (activeTab === "all") return referrals;
    return referrals.filter((ref) => ref.status?.toLowerCase() === activeTab);
  };

  // Program rewards data
  const programRewards = [
    { program: "Tech Pro", amount: 1000 },
    { program: "Tech Smart", amount: 500 },
    { program: "Data Science", amount: 1000 },
  ];

  return (
    <div className="rc-container">
      {/* HEADER */}
      <div className="rc-header">
        <div className="rc-header-content">
          <div className="rc-heading-container">
            <h1 className="rc-heading">Your Referrals</h1>
            <button
              className="rc-info-icon-btn"
              onClick={() => setShowInfoModal(true)}
            >
              <Info size={20} />
            </button>
          </div>
          <p className="rc-subtitle">Track your referral status and earnings</p>
        </div>

        <div className="rc-stats">
          <div className="rc-stat">
            <Users size={24} />
            <span>{referrals.length} Referrals</span>
          </div>
          <div className="rc-stat earnings">
            <IndianRupee size={24} />
            <span>₹{calculateEarnings()} Earned</span>
          </div>
          {/* <div className="rc-stat wallet">
            <Wallet size={24} />
            <span>₹{walletBalance} Wallet</span>
          </div> */}
        </div>

        <button
          className="rc-refer-now-btn"
          onClick={() => setShowPopupForm(true)}
        >
          + Refer Someone
        </button>
      </div>

      {/* <button
        className={`withdraw-btn ${walletBalance === 0 ? "disabled" : ""}`}
        onClick={handleWithdrawRequest}
        disabled={walletBalance === 0 || withdrawLoading}
      >
        {withdrawLoading ? "Processing..." : "Request Withdrawal"}
      </button> */}

      {/* WALLET SECTION */}
      {/* <div className="wallet-section">
        <div className="wallet-card">
          <div className="wallet-header">
            <Wallet size={32} className="wallet-icon" />
            <h3>Your Wallet</h3>
          </div>
          <div className="wallet-balance">
            <span className="balance-label">Available Balance</span>
            <div className="balance-amount">₹{walletBalance}</div>
          </div>
        </div>
      </div> */}

      {/* ============================
            INFO MODAL
      ============================ */}
      {showInfoModal && (
        <div className="info-modal-overlay">
          <div className="info-modal">
            <button
              className="info-modal-close"
              onClick={() => setShowInfoModal(false)}
            >
              ×
            </button>

            <h2 className="info-modal-title">
              <Info size={24} />
              Referral Program Guide
            </h2>

            {/* Info Tabs */}
            <div className="info-modal-tabs">
              <button
                className={`info-tab ${
                  activeInfoTab === "stages" ? "active" : ""
                }`}
                onClick={() => setActiveInfoTab("stages")}
              >
                Referral Stages
              </button>
              <button
                className={`info-tab ${
                  activeInfoTab === "rewards" ? "active" : ""
                }`}
                onClick={() => setActiveInfoTab("rewards")}
              >
                Reward Amounts
              </button>
            </div>

            {/* Tab Content */}
            <div className="info-tab-content">
              {activeInfoTab === "stages" && (
                <div className="referral-stages">
                  <h3>Referral Stages:</h3>

                  <div className="stage-item">
                    <div className="stage-header">
                      <div className="stage-indicator pending"></div>
                      <h4>Pending</h4>
                    </div>
                    <p>Successfully referred a student - initial stage</p>
                  </div>

                  <div className="stage-item">
                    <div className="stage-header">
                      <div className="stage-indicator yet-to-join"></div>
                      <h4>Yet to Join</h4>
                    </div>
                    <p>
                      Our experts have contacted the student, waiting for
                      confirmation
                    </p>
                  </div>

                  <div className="stage-item">
                    <div className="stage-header">
                      <div className="stage-indicator joined"></div>
                      <h4>Joined</h4>
                    </div>
                    <p>
                      Student has enrolled in a program - referral amount added
                      to your wallet
                    </p>
                  </div>

                  <div className="stage-item">
                    <div className="stage-header">
                      <div className="stage-indicator completed"></div>
                      <h4>Completed</h4>
                    </div>
                    <p>Payment successfully sent to your bank account</p>
                  </div>

                  <div className="stage-item">
                    <div className="stage-header">
                      <div className="stage-indicator rejected"></div>
                      <h4>Rejected</h4>
                    </div>
                    <p>
                      Student didn't join any program or didn't meet criteria
                    </p>
                  </div>
                </div>
              )}

              {activeInfoTab === "rewards" && (
                <div className="rewards-section">
                  <h3>Referral Rewards by Program</h3>
                  <div className="rewards-grid">
                    {programRewards.map((program, index) => (
                      <div key={index} className="reward-item">
                        <div className="program-name">{program.program}</div>
                        <div className="program-reward">₹{program.amount}</div>
                      </div>
                    ))}
                  </div>

                  <div className="rewards-notes">
                    <h4>Important Notes:</h4>
                    <ul>
                      <li>Reward amount is per successful referral</li>
                      <li>
                        Payment is processed only when referral status is
                        "Completed"
                      </li>
                      <li>
                        Amount will be credited to your bank account in the
                        first week of next month
                      </li>
                      <li>Our team will reach out to you for bank details</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="current-program-reward">
              <div className="reward-badge">
                <Gift size={20} />
                <span>
                  Your Program ({user.student.program}): ₹{rewardAmount} per
                  referral
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================
            POPUP FORM OVERLAY
      ============================ */}
      {showPopupForm && (
        <div className="popup-overlay">
          <div className="popup-box">
            <button
              className="popup-close"
              onClick={() => setShowPopupForm(false)}
            >
              ×
            </button>

            <h2 className="popup-title">Refer & Earn</h2>
            <p className="popup-reward">
              Earn <strong>₹{rewardAmount}</strong> per successful referral 🎁
            </p>

            <form className="popup-form" onSubmit={submitReferral}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={user.student.studentName}
                onChange={handleReferralChange}
                disabled
              />

              <input
                type="text"
                name="referralName"
                placeholder="Referral Name"
                value={referralData.referralName}
                onChange={handleReferralChange}
                required
                disabled={submitting}
              />

              <input
                type="tel"
                name="referralNumber"
                placeholder="Referral Contact Number"
                value={referralData.referralNumber}
                onChange={handleReferralChange}
                required
                disabled={submitting}
              />

              <button
                type="submit"
                className={`popup-submit ${submitting ? "loading" : ""}`}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Referral"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* FILTER TABS */}
      <div className="rc-tabs">
        {[
          { label: "All", value: "all" },
          { label: "Pending", value: "pending" },
          { label: "Yet to Join", value: "yet to join" },
          { label: "Joined", value: "joined" },
          { label: "Completed", value: "completed" },
          { label: "Rejected", value: "rejected" },
        ].map((tab) => (
          <button
            key={tab.value}
            className={`rc-tab-btn ${activeTab === tab.value ? "active" : ""}`}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* SUCCESS POPUP */}
      {successPopup && (
        <div className="success-toast">🎉 Referral Submitted Successfully!</div>
      )}

      {/* LOADING */}
      {loading ? (
        <div className="rc-loading-container">
          <div className="rc-loading-spinner"></div>
          <p className="rc-loading-text">Loading your referrals...</p>
        </div>
      ) : referrals.length === 0 ? (
        <div className="rc-empty-state">
          <Users size={64} className="rc-empty-icon" />
          <h3 className="rc-empty-title">No Referrals Yet</h3>
          <p className="rc-empty-description">
            Start referring friends to earn rewards!
          </p>
          <button
            className="rc-refer-now-btn"
            onClick={() => setShowPopupForm(true)}
          >
            Refer Now
          </button>
        </div>
      ) : (
        <>
          {/* CARDS GRID */}
          <div className="rc-grid">
            {getFilteredReferrals().map((referral, index) => (
              <div
                key={referral._id}
                className={`rc-card ${
                  flippedCards.has(referral._id) ? "flipped" : ""
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => handleCardFlip(referral._id)}
              >
                <div className="rc-card-inner">
                  {/* FRONT SIDE */}
                  <div className="rc-card-front">
                    <div className="rc-card-header">
                      <div className="rc-card-badge">REFERRAL</div>
                      <div
                        className={`rc-status ${getStatusColor(
                          referral.status
                        )}`}
                      >
                        {getStatusIcon(referral.status)}
                        {referral.status || "Pending"}
                      </div>
                    </div>

                    <div className="rc-card-content">
                      <div className="rc-referral-avatar">
                        <User size={32} />
                      </div>

                      <h3 className="rc-referral-name">
                        {referral.referralName}
                      </h3>

                      <div className="rc-referral-info">
                        <div className="rc-info-row">
                          <Phone size={16} />
                          <span>{referral.referralNumber}</span>
                        </div>

                        <div className="rc-info-row">
                          <Calendar size={16} />
                          <span>
                            {new Date(referral.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rc-card-footer">
                      <div className="rc-tap-hint">Tap card for details</div>
                    </div>
                  </div>

                  {/* BACK SIDE */}
                  <div className="rc-card-back">
                    <div className="rc-back-content">
                      {/* Your Details */}
                      <div className="rc-details-section">
                        <h4 className="rc-section-title">Your Details</h4>
                        <div className="rc-detail-item">
                          <div className="rc-detail-label">
                            <User size={14} />
                            <span>Name</span>
                          </div>
                          <div className="rc-detail-value">{referral.name}</div>
                        </div>
                        <div className="rc-detail-item">
                          <div className="rc-detail-label">
                            <span className="rc-uid-icon">ID</span>
                            <span>Student ID</span>
                          </div>
                          <div className="rc-detail-value uid-value">
                            {referral.UID}
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="rc-status-section">
                        <h4 className="rc-section-title">Referral Status</h4>
                        <div className="rc-status-display">
                          <div
                            className={`rc-status-badge ${getStatusColor(
                              referral.status
                            )}`}
                          >
                            {getStatusIcon(referral.status)}
                            {referral.status || "Pending"}
                          </div>
                          <p className="rc-status-description">
                            {referral.status?.toLowerCase() === "completed"
                              ? `Congratulations! You've earned ₹${rewardAmount}`
                              : referral.status?.toLowerCase() === "rejected"
                              ? "This referral didn't meet the criteria"
                              : referral.status?.toLowerCase() === "yet to join"
                              ? "Our team has contacted the student, waiting for confirmation"
                              : referral.status?.toLowerCase() === "joined"
                              ? "Student has enrolled! Amount will be credited next month"
                              : "Waiting for your friend to complete the process"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rc-card-footer">
                      <div className="rc-tap-hint">Tap to flip back</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Referrals;
